import { useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Download, X, Smartphone } from 'lucide-react';

// Helper to check iOS
const checkIsIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

// Helper to check standalone mode
const checkIsStandalone = () => 
  window.matchMedia('(display-mode: standalone)').matches || 
  window.navigator.standalone === true;

// Helper to check if iOS banner should show
const shouldShowIOSBannerInitially = () => {
  const isIOS = checkIsIOS();
  const isStandalone = checkIsStandalone();
  if (!isIOS || isStandalone) return false;
  const dismissed = localStorage.getItem('pwa-install-dismissed-ios');
  return !dismissed;
};

const InstallPWA = () => {
  const { pathname } = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  // Initialize banner state directly based on iOS check
  const [showInstallBanner, setShowInstallBanner] = useState(shouldShowIOSBannerInitially);

  // Check if iOS - computed once
  const isIOS = useMemo(() => checkIsIOS(), []);

  // Check if already installed (standalone mode)
  const isStandalone = useMemo(() => checkIsStandalone(), []);

  useEffect(() => {
    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Check if user has dismissed the banner before
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed) {
        setShowInstallBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const handleDismiss = () => {
    setShowInstallBanner(false);
    if (isIOS) {
      localStorage.setItem('pwa-install-dismissed-ios', 'true');
    } else {
      localStorage.setItem('pwa-install-dismissed', 'true');
    }
  };

  // Don't show if already installed
  if (['/', '/login', '/register'].includes(pathname) || isStandalone || !showInstallBanner) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up"
    >
      <Card className="shadow-lg border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm">Install Kos Cemara</h3>
              {isIOS ? (
                <p className="text-xs text-muted-foreground mt-1">
                  Tap <span className="inline-flex items-center"><svg className="w-4 h-4 mx-1" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L8 6h3v8h2V6h3L12 2zm-7 9v11h14V11h-2v9H7v-9H5z"/></svg></span> 
                  lalu &quot;Add to Home Screen&quot;
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">
                  Akses lebih cepat langsung dari home screen HP Anda
                </p>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 flex-shrink-0"
              onClick={handleDismiss}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {!isIOS && (
            <div className="mt-3 flex gap-2">
              <Button 
                onClick={handleInstallClick} 
                className="flex-1 rounded-xl"
                size="sm"
                data-testid="install-pwa-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Install Sekarang
              </Button>
              <Button 
                variant="outline" 
                onClick={handleDismiss}
                className="rounded-xl"
                size="sm"
              >
                Nanti
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InstallPWA;
